import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const AdminDashboard = () => {
    const [bookings, setBookings] = useState([]);
    const [primaryPhotos, setPrimaryPhotos] = useState([]);
    const [cars, setCars] = useState([]);
    const [filter, setFilter] = useState("booked");
    const [selectedCar, setSelectedCar] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);
    const [isBlocking, setIsBlocking] = useState(false);
    const navigate = useNavigate();
    const logoutTimerRef = useRef(null);
    const formRef = useRef(null);
    const [showExistingCars, setShowExistingCars] = useState(false);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [expandedFeatures, setExpandedFeatures] = useState({});



    const backendBaseUrl = "https://api.karrentals.ca";

    useEffect(() => {
        fetchBookings();
        fetchPrimaryPhotos();
        fetchCars();
    }, [filter]);

    useEffect(() => {
        startLogoutTimer();
        return () => stopLogoutTimer();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await axios.get(`${backendBaseUrl}/fetch-all-bookings`);
            const bookings = response.data.bookings;

            const updatedBookings = await Promise.all(
                bookings.map(async (booking) => {
                    try {
                        const carResponse = await axios.get(`${backendBaseUrl}/search-cars`, {
                            params: { car_id: booking.car_id },
                        });
                        const carDetails = carResponse.data.data[0];
                        return {
                            ...booking,
                            carName: carDetails?.name || "Unknown Name",
                            carYear: carDetails?.year || "Unknown Year",
                            carModel: carDetails?.model || "Unknown Model",
                        };
                    } catch (error) {
                        console.error(`Error fetching car details for car_id ${booking.car_id}:`, error);
                        return {
                            ...booking,
                            carName: "Unknown Name",
                            carYear: "Unknown Year",
                            carModel: "Unknown Model",
                        };
                    }
                })
            );

            setBookings(updatedBookings);
        } catch (error) {
            console.error("Error fetching bookings:", error);
        }
    };

    const fetchPrimaryPhotos = async () => {
        try {
            const response = await axios.get(`${backendBaseUrl}/primary-photos`);
            setPrimaryPhotos(response.data.data);
        } catch (error) {
            console.error("Error fetching primary photos:", error);
        }
    };

    const fetchCars = async () => {
        try {
            const response = await axios.get(`${backendBaseUrl}/fetch-cars`);
            setCars(response.data.cars);
        } catch (error) {
            console.error("Error fetching cars:", error);
        }
    };

    const changeStatus = async (bookingId, newStatus) => {
        const endpoint = newStatus === "booked" ? "/accept-cancelled-booking" : "/cancel-booking";
        try {
            await axios.post(`${backendBaseUrl}${endpoint}`, { bookingId });
            fetchBookings();
        } catch (error) {
            console.error(`Error updating booking with ID ${bookingId}:`, error);
        }
    };

    // License images live in a private S3 bucket, so the stored URL alone
    // isn't viewable — exchange it for a signed GET URL first. Open the tab
    // synchronously (before the await) so browsers don't treat it as a
    // blocked popup, then navigate it once the signed URL comes back.
    const openLicenseImage = async (rawUrl) => {
        if (!rawUrl) return;
        const tab = window.open("", "_blank");
        try {
            const { data } = await axios.post(`${backendBaseUrl}/license-presigned-url`, { url: rawUrl });
            if (data.success && tab) {
                tab.location.href = data.url;
            } else if (tab) {
                tab.close();
                alert("Could not load this license image. It may not have uploaded successfully.");
            }
        } catch (error) {
            console.error("Error fetching license image URL:", error);
            if (tab) tab.close();
            alert("Could not load this license image. It may not have uploaded successfully.");
        }
    };

    const handleAddMaintenanceBlock = async () => {
        if (!selectedCar || selectedDates.length < 2) {
            alert("Please select a car and valid start and end dates.");
            return;
        }

        try {
            await axios.post(`${backendBaseUrl}/add-maintenance-block`, {
                car_id: selectedCar,
                start_date: selectedDates[0].toISOString(),
                end_date: selectedDates[1].toISOString(),
            });
            alert("Maintenance block added successfully.");
            fetchBookings();
            setIsBlocking(false);
            setSelectedCar(null);
            setSelectedDates([]);
        } catch (error) {
            console.error("Error adding maintenance block:", error);
            alert("Failed to add maintenance block. Please try again.");
        }
    };

    const startLogoutTimer = () => {
        logoutTimerRef.current = setTimeout(() => {
            handleLogout();
        }, 15 * 60 * 1000);
    };

    const stopLogoutTimer = () => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("adminToken");
        navigate("/admin-login");
    };

    const filteredBookings = bookings.filter((booking) => booking.status === filter);

    const formatDateTime = (date) => {
        return new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).format(new Date(date));
    };

    const getPrimaryPhoto = (carId) => {
        return primaryPhotos.find((photo) => photo.car_id === carId)?.primary_photo_url || null;
    };

    const [isAddingCar, setIsAddingCar] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [primaryPhotoFile, setPrimaryPhotoFile] = useState(null);
    const [additionalPhotoFiles, setAdditionalPhotoFiles] = useState([]);
    const [existingPhotos, setExistingPhotos] = useState([]); // photos already in DB for this car
    const [photoToConfirmDelete, setPhotoToConfirmDelete] = useState(null); // photo_url pending deletion confirm
    const [carFolder, setCarFolder] = useState(""); // resolved S3 folder for current edit session

    const [newCar, setNewCar] = useState({
        name: "",
        model: "",
        location: "",
        price_per_day: "",
        engine_type: "",
        bags: "",
        passengers: "",
        description: "",
        doors: "",
        available_from: "",
        available_to: "2035-12-31",
        year: "",
        features: "",
    });

    const handleAddCarChange = (e) => {
        const { name, value } = e.target;
        setNewCar((prev) => ({ ...prev, [name]: value }));
    };

    const emptyCarForm = {
        name: "",
        model: "",
        location: "",
        price_per_day: "",
        engine_type: "",
        bags: "",
        passengers: "",
        description: "",
        doors: "",
        available_from: "",
        available_to: "2035-12-31",
        year: "",
        features: "",
    };

    // Converts any image file (PNG, HEIC, JPEG, WebP…) to a JPEG Blob via Canvas.
    // HEIC works natively on Safari/macOS; on other browsers it falls back gracefully.
    const convertToJpeg = (file) =>
        new Promise((resolve) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(url);
                    resolve(blob || file); // fallback to original if conversion fails
                }, 'image/jpeg', 0.92);
            };
            img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
            img.src = url;
        });

    // Upload a single photo to S3 via presigned URL.
    // Converts to JPEG first so any input format (PNG, HEIC, etc.) lands as .JPG in S3.
    // purpose: 'primary' | 1 | 2 | 3 ... (number index for additional shots)
    const uploadCarPhotoToS3 = async (file, folder, purpose) => {
        const jpeg = await convertToJpeg(file);
        const { data } = await axios.post(`${backendBaseUrl}/generate-car-photo-url`, {
            car_folder: folder,
            purpose,
        });
        await fetch(data.url, {
            method: 'PUT',
            body: jpeg,
            headers: { 'Content-Type': 'image/jpeg' },
        });
        return data.key; // e.g. "mercedes/primary.JPG" or "mercedes/1.JPG"
    };

    const handleAddCar = async () => {
        const isEditing = !!newCar.id;

        // Strip out any UI-only fields before sending to the API
        const { primary_image, additional_images, ...carPayload } = newCar;

        setIsSaving(true);
        try {
            let carId;
            if (isEditing) {
                await axios.post(`${backendBaseUrl}/edit-car`, carPayload);
                carId = newCar.id;
            } else {
                const res = await axios.post(`${backendBaseUrl}/add-car`, carPayload);
                carId = res.data.data.id;
            }

            // Upload any selected photos to S3 and record them in car_photos
            const hasNewPhotos = primaryPhotoFile || additionalPhotoFiles.length > 0;
            if (hasNewPhotos) {
                let resolvedFolder = carFolder;

                if (!isEditing) {
                    // For new cars, ask the backend for a unique folder (uses name + model)
                    const { data } = await axios.post(`${backendBaseUrl}/resolve-car-folder`, {
                        car_name: `${newCar.name} ${newCar.model}`.trim(),
                    });
                    resolvedFolder = data.folder;
                }

                // For edit mode: find the highest numbered photo already in the DB
                // so new additional photos continue the sequence (e.g. if 1-5 exist, start at 6)
                let nextPhotoIndex = 1;
                if (isEditing && existingPhotos.length > 0) {
                    const maxExisting = existingPhotos.reduce((max, p) => {
                        const filename = p.photo_url.split('/').pop().replace('.JPG', '');
                        const num = parseInt(filename, 10);
                        return isNaN(num) ? max : Math.max(max, num);
                    }, 0);
                    nextPhotoIndex = maxExisting + 1;
                }

                const photoKeys = [];
                if (primaryPhotoFile) {
                    const key = await uploadCarPhotoToS3(primaryPhotoFile, resolvedFolder, 'primary');
                    photoKeys.push({ key });
                }
                for (let i = 0; i < additionalPhotoFiles.length; i++) {
                    const key = await uploadCarPhotoToS3(additionalPhotoFiles[i], resolvedFolder, nextPhotoIndex + i);
                    photoKeys.push({ key });
                }

                await axios.post(`${backendBaseUrl}/add-car-photos`, {
                    car_id: carId,
                    photos: photoKeys,
                });
            }

            setIsAddingCar(false);
            setNewCar(emptyCarForm);
            setPrimaryPhotoFile(null);
            setAdditionalPhotoFiles([]);
            setExistingPhotos([]);
            setCarFolder("");
            fetchCars();
            alert(isEditing ? "Car updated successfully." : "Car added successfully.");
        } catch (error) {
            console.error("Error saving car:", error);
            alert("Failed to save the car. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditCar = async (car) => {
        // Fetch existing photos so we can display them in the form
        let photos = [];
        try {
            const res = await axios.get(`${backendBaseUrl}/car-photos/${car.id}`);
            const raw = res.data.photos || [];
            // Normalise: backend may return objects {photo_url, full_url} OR plain URL strings
            photos = raw.map((p) => {
                if (typeof p === "string") {
                    // old format — derive relative key from the full URL
                    const photo_url = p.replace("https://karrental.s3.amazonaws.com/", "");
                    return { photo_url, full_url: p };
                }
                return p; // already {photo_url, full_url}
            });
        } catch (e) {
            console.error("Could not fetch existing photos:", e);
        }
        setExistingPhotos(photos);
        setPrimaryPhotoFile(null);
        setAdditionalPhotoFiles([]);

        // Derive the folder from the first photo's key, e.g. "mercedes/primary.JPG" → "mercedes"
        const folderFromPhoto = photos.length > 0
            ? photos[0].photo_url.split("/")[0]
            : "";
        setCarFolder(folderFromPhoto);

        setNewCar({
            id: car.id,
            name: car.name || "",
            model: car.model || "",
            location: car.location || "",
            price_per_day: car.price_per_day || "",
            engine_type: car.engine_type || "",
            bags: car.bags || "",
            passengers: car.passengers || "",
            description: car.description || "",
            doors: car.doors || "",
            available_from: car.available_from?.split("T")[0] || "",
            available_to: car.available_to?.split("T")[0] || "2035-12-31",
            year: car.year || "",
            features: car.features || "",
        });
        setIsAddingCar(true);
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    // Delete a single photo from the DB (called from the existing photos strip in the form)
    const handleDeleteSinglePhoto = async () => {
        if (!photoToConfirmDelete) return;
        try {
            await axios.delete(`${backendBaseUrl}/car-photo-single`, {
                data: { car_id: newCar.id, photo_url: photoToConfirmDelete.photo_url },
            });
            setExistingPhotos((prev) => prev.filter((p) => p.photo_url !== photoToConfirmDelete.photo_url));
        } catch (e) {
            console.error("Error deleting photo:", e);
            alert("Failed to delete photo. Please try again.");
        } finally {
            setPhotoToConfirmDelete(null);
        }
    };

    const handleDeleteCar = async (carId) => {
        if (window.confirm("Are you sure you want to delete this car?")) {
            try {
                await axios.delete(`${backendBaseUrl}/delete-car`, {
                    data: { car_id: carId },
                });
                alert("Car deleted successfully.");
                fetchCars();
            } catch (error) {
                console.error("Error deleting car:", error);
                alert("Failed to delete the car. Please try again.");
            }
        }
    };

    const toggleDescription = (id) => {
        setExpandedDescriptions((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const toggleFeatures = (id) => {
        setExpandedFeatures((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    return (
        <Layout>
            {/* Photo deletion confirmation dialog */}
            {photoToConfirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                        <h3 className="text-lg font-bold text-black mb-3">Delete Photo?</h3>
                        <div className="flex justify-center mb-4">
                            <img
                                src={photoToConfirmDelete.full_url}
                                alt="To delete"
                                className="w-32 h-32 object-cover rounded border"
                            />
                        </div>
                        <p className="text-gray-700 text-sm mb-5 text-center">
                            This will remove <strong>{photoToConfirmDelete.photo_url}</strong> from the database.
                            The S3 file will remain but will no longer be shown.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setPhotoToConfirmDelete(null)}
                                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteSinglePhoto}
                                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto my-6 p-6">
                <div className="bg-white p-6 rounded shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl text-black font-bold">Admin Dashboard</h1>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setIsBlocking((prev) => !prev)}
                                className="text-sm px-4 py-2 bg-black text-white rounded"
                            >
                                {isBlocking ? "X" : "+"}
                            </button>
                            <button
                                onClick={() => setShowExistingCars(prev => !prev)}
                                className="text-sm px-4 py-2 bg-black text-white rounded"
                            >
                                {showExistingCars ? "Hide Cars" : "Show Existing Cars"}
                            </button>
                            <button
                                onClick={() => setIsAddingCar((prev) => !prev)}
                                className="text-sm px-4 py-2 bg-black text-white rounded flex items-center space-x-2"
                            >
                               <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M16.22 3H7.78C6.6 3 5.58 3.9 5.27 5.05L4.1 9.05C3.87 9.91 4.12 10.81 4.75 11.41C4.29 11.79 4 12.38 4 13V16C4 16.55 4.45 17 5 17H6C6.55 17 7 16.55 7 16V15H17V16C17 16.55 17.45 17 18 17H19C19.55 17 20 16.55 20 16V13C20 12.38 19.71 11.79 19.25 11.41C19.88 10.81 20.13 9.91 19.9 9.05L18.73 5.05C18.42 3.9 17.4 3 16.22 3ZM6.5 14.5C5.67 14.5 5 13.83 5 13C5 12.17 5.67 11.5 6.5 11.5C7.33 11.5 8 12.17 8 13C8 13.83 7.33 14.5 6.5 14.5ZM17.5 14.5C16.67 14.5 16 13.83 16 13C16 12.17 16.67 11.5 17.5 11.5C18.33 11.5 19 12.17 19 13C19 13.83 18.33 14.5 17.5 14.5ZM6.61 5.5H17.39L18.2 8H5.8L6.61 5.5Z"
                                    />
                                </svg>
                                <span>+{isAddingCar ? "Cancel" : ""}</span>
                            </button>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                    {isAddingCar && (
                        <>
                            <div ref={formRef} className="mb-6 bg-black p-4 rounded">
                                <h3 className="text-lg font-bold mb-4">{newCar.id ? "Edit Car" : "Add New Car"}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Car Name"
                                        value={newCar.name}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="text"
                                        name="model"
                                        placeholder="Car Model"
                                        value={newCar.model}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="text"
                                        name="location"
                                        placeholder="Location"
                                        value={newCar.location}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="number"
                                        name="price_per_day"
                                        placeholder="Price per Day ($)"
                                        value={newCar.price_per_day}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="text"
                                        name="engine_type"
                                        placeholder="Engine Type"
                                        value={newCar.engine_type}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="number"
                                        name="bags"
                                        placeholder="Number of Bags"
                                        value={newCar.bags}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="number"
                                        name="passengers"
                                        placeholder="Number of Passengers"
                                        value={newCar.passengers}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="number"
                                        name="doors"
                                        placeholder="Number of Doors"
                                        value={newCar.doors}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="date"
                                        name="available_from"
                                        placeholder="Available From"
                                        value={newCar.available_from}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <input
                                        type="number"
                                        name="year"
                                        placeholder="Year"
                                        value={newCar.year}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <textarea
                                        name="description"
                                        placeholder="Description"
                                        value={newCar.description}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    <textarea
                                        name="features"
                                        placeholder="Features (comma-separated)"
                                        value={newCar.features}
                                        onChange={handleAddCarChange}
                                        className="w-full text-black border rounded px-3 py-2"
                                    />
                                    {/* Existing Photos (edit mode only) */}
                                    {newCar.id && (
                                        <div className="sm:col-span-2">
                                            <label className="block text-white text-sm font-medium mb-2">Current Photos</label>
                                            {existingPhotos.length === 0 ? (
                                                <p className="text-gray-400 text-sm italic">No photos uploaded for this car yet.</p>
                                            ) : (
                                                <div className="flex flex-wrap gap-3">
                                                    {existingPhotos.map((photo) => (
                                                        <div key={photo.photo_url} className="relative">
                                                            <img
                                                                src={photo.full_url}
                                                                alt={photo.photo_url}
                                                                className="w-28 h-28 object-cover rounded border border-gray-600"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setPhotoToConfirmDelete(photo)}
                                                                className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold leading-none shadow"
                                                                title="Delete photo"
                                                            >×</button>
                                                            <p className="text-gray-400 text-xs mt-1 max-w-[112px] truncate text-center">
                                                                {photo.photo_url.split("/").pop()}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Primary Photo Upload */}
                                    <div className="sm:col-span-2">
                                        <label className="block text-white text-sm font-medium mb-1">
                                            Primary Photo <span className="text-gray-400 font-normal">(JPG, PNG, HEIC accepted — auto-converted to JPG)</span>
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*,.heic,.HEIC"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (!file) { setPrimaryPhotoFile(null); return; }
                                                setPrimaryPhotoFile(file);
                                            }}
                                            className="w-full text-white border border-gray-600 rounded px-3 py-2 bg-gray-800"
                                        />
                                        {primaryPhotoFile && (
                                            <p className="text-green-400 text-xs mt-1">✓ {primaryPhotoFile.name}</p>
                                        )}
                                    </div>
                                    {/* Additional Photos Upload */}
                                    <div className="sm:col-span-2">
                                        <label className="block text-white text-sm font-medium mb-1">
                                            Additional Photos <span className="text-gray-400 font-normal">(JPG, PNG, HEIC accepted — multiple allowed)</span>
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*,.heic,.HEIC"
                                            multiple
                                            onChange={(e) => {
                                                setAdditionalPhotoFiles(Array.from(e.target.files));
                                            }}
                                            className="w-full text-white border border-gray-600 rounded px-3 py-2 bg-gray-800"
                                        />
                                        {additionalPhotoFiles.length > 0 && (
                                            <p className="text-green-400 text-xs mt-1">✓ {additionalPhotoFiles.length} file(s) selected</p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={handleAddCar}
                                    disabled={isSaving}
                                    className={`mt-4 px-4 py-2 text-white rounded flex items-center gap-2 ${isSaving ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                                >
                                    {isSaving && (
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                        </svg>
                                    )}
                                    {isSaving
                                        ? (primaryPhotoFile || additionalPhotoFiles.length > 0 ? "Uploading photos…" : "Saving…")
                                        : (newCar.id ? "Update Car" : "Save Car")}
                                </button>
                            </div>
                        </>
                    )}
                    {showExistingCars && (
                        <>
                            <h3 className="text-lg font-bold mt-10 mb-4 text-black">Existing Cars</h3>
                            <div className="overflow-x-auto">
                            <table className="mb-6 min-w-full bg-black text-white rounded text-sm">
                                <thead>
                                <tr>
                                    <th className="px-3 py-2 border">ID</th>
                                    <th className="px-3 py-2 border">Name</th>
                                    <th className="px-3 py-2 border">Model</th>
                                    <th className="px-3 py-2 border">Location</th>
                                    <th className="px-3 py-2 border">Price/Day</th>
                                    <th className="px-3 py-2 border">Engine</th>
                                    <th className="px-3 py-2 border">Year</th>
                                    <th className="px-3 py-2 border">Doors</th>
                                    <th className="px-3 py-2 border">Bags</th>
                                    <th className="px-3 py-2 border">Passengers</th>
                                    <th className="px-3 py-2 border">From</th>
                                    <th className="px-3 py-2 border">To</th>
                                    <th className="px-3 py-2 border">Features</th>
                                    <th className="px-3 py-2 border">Description</th>
                                    <th className="px-3 py-2 border">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {cars.map((car) => (
                                    <tr key={car.id}>
                                    <td className="px-3 py-2 border">{car.id}</td>
                                    <td className="px-3 py-2 border">{car.name}</td>
                                    <td className="px-3 py-2 border">{car.model}</td>
                                    <td className="px-3 py-2 border">{car.location}</td>
                                    <td className="px-3 py-2 border">${car.price_per_day}</td>
                                    <td className="px-3 py-2 border">{car.engine_type}</td>
                                    <td className="px-3 py-2 border">{car.year}</td>
                                    <td className="px-3 py-2 border">{car.doors}</td>
                                    <td className="px-3 py-2 border">{car.bags}</td>
                                    <td className="px-3 py-2 border">{car.passengers}</td>
                                    <td className="px-3 py-2 border">{car.available_from?.split("T")[0]}</td>
                                    <td className="px-3 py-2 border">{car.available_to?.split("T")[0]}</td>
                                    <td className="px-4 py-2 border max-w-xs whitespace-pre-wrap break-words">
                                        {car.features
                                            ? <>
                                                {expandedFeatures[car.id]
                                                    ? car.features
                                                    : `${car.features.slice(0, 100)}${car.features.length > 100 ? "..." : ""}`}
                                                {car.features.length > 100 && (
                                                    <button
                                                        onClick={() => toggleFeatures(car.id)}
                                                        className="text-blue-300 ml-2 underline"
                                                    >
                                                        {expandedFeatures[car.id] ? "Show less" : "Show more"}
                                                    </button>
                                                )}
                                              </>
                                            : <span className="text-gray-400">—</span>
                                        }
                                    </td>
                                    <td className="px-4 py-2 border max-w-xs whitespace-pre-wrap break-words">
                                        {car.description
                                            ? <>
                                                {expandedDescriptions[car.id]
                                                    ? car.description
                                                    : `${car.description.slice(0, 100)}${car.description.length > 100 ? "..." : ""}`}
                                                {car.description.length > 100 && (
                                                    <button
                                                        onClick={() => toggleDescription(car.id)}
                                                        className="text-blue-300 ml-2 underline"
                                                    >
                                                        {expandedDescriptions[car.id] ? "Show less" : "Show more"}
                                                    </button>
                                                )}
                                              </>
                                            : <span className="text-gray-400">—</span>
                                        }
                                    </td>
                                    <td className="px-3 py-2 border">
                                        <div className="flex flex-col gap-1 items-center">
                                        <button
                                        onClick={() => handleEditCar(car)}
                                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-1 rounded text-sm font-medium"
                                        >
                                        Edit
                                        </button>
                                        <button
                                        onClick={() => handleDeleteCar(car.id)}
                                        className="w-full bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-medium"
                                        >
                                        Delete
                                        </button>
                                        </div>
                                    </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            </div>
                        </>
                    )}
                    {isBlocking && (
                        <div className="mb-6 bg-black p-4 rounded">
                            <h3 className="text-lg font-bold mb-4">Add Maintenance Block</h3>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Select Car</label>
                                <select
                                    value={selectedCar || ""}
                                    onChange={(e) => setSelectedCar(e.target.value)}
                                    className="w-full border text-black rounded px-3 py-2"
                                >
                                    <option value="" disabled>
                                        Choose a car
                                    </option>
                                    {cars.map((car) => (
                                        <option key={car.id} value={car.id}>
                                            {car.year} {car.name} {car.model}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-4 text-black">
                                <label className="block text-sm text-white font-medium mb-2">Select Dates</label>
                                <Calendar
                                    selectRange
                                    onChange={setSelectedDates}
                                    tileDisabled={({ date }) => {
                                        if (date < new Date().setHours(0, 0, 0, 0)) {
                                            return true;
                                        }
                                        if (selectedCar) {
                                            return bookings.some(
                                                (booking) =>
                                                    booking.car_id === parseInt(selectedCar) &&
                                                    booking.status !== "cancelled" && 
                                                    date >= new Date(booking.start_date).setHours(0, 0, 0, 0) &&
                                                    date <= new Date(booking.end_date).setHours(0, 0, 0, 0)
                                            );
                                        }
                                        return false;
                                    }}
                                />
                            </div>
                            <button
                                onClick={handleAddMaintenanceBlock}
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                            >
                                Save Maintenance Block
                            </button>
                        </div>
                    )}
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex">
                            <button
                                onClick={() => setFilter("booked")}
                                className={`px-4 py-2 mr-2 rounded ${
                                    filter === "booked" ? "bg-black text-white" : "bg-gray-300"
                                }`}
                            >
                                Booked
                            </button>
                            <button
                                onClick={() => setFilter("cancelled")}
                                className={`px-4 py-2 mr-2 rounded ${
                                    filter === "cancelled" ? "bg-black text-white" : "bg-gray-300"
                                }`}
                            >
                                Cancelled
                            </button>
                            <button
                                onClick={() => setFilter("completed")}
                                className={`px-4 py-2 rounded ${
                                    filter === "completed" ? "bg-black text-white" : "bg-gray-300"
                                }`}
                            >
                                Completed
                            </button>
                        </div>
                    </div>
                    {filteredBookings.length > 0 ? (
                        filteredBookings.map((booking) => (
                            <div
                                key={booking.id}
                                className="p-4 border rounded mb-4 bg-black shadow-md flex flex-col sm:flex-col md:flex-row md:items-start md:justify-between text-white"
                            >
                                {/* Image */}
                                {getPrimaryPhoto(booking.car_id) && (
                                    <div className="flex-shrink-0 w-full sm:w-60 h-60 mb-4 mr-4 sm:mb-0">
                                        <img
                                            src={getPrimaryPhoto(booking.car_id)}
                                            alt="Car"
                                            className="rounded w-full h-full object-cover"
                                        />
                                    </div>
                                )}

                                {/* Details */}
                                <div className="w-full">
                                    <p>
                                        <strong>Booking ID:</strong> {booking.id}
                                    </p>
                                    <p>
                                        <strong>Booking Date:</strong> {formatDateTime(booking.booking_date)}
                                    </p>
                                    <p>
                                        <strong>Car:</strong> {booking.carYear} {booking.carName} {booking.carModel}
                                    </p>
                                    <p>
                                        <strong>Start Date:</strong> {formatDateTime(booking.start_date)}
                                    </p>
                                    <p>
                                        <strong>End Date:</strong> {formatDateTime(booking.end_date)}
                                    </p>
                                    <p>
                                        <strong>Total:</strong> ${parseFloat(booking.total_price || 0).toFixed(2)}
                                    </p>
                                    <p>
                                        <strong>Customer:</strong> {booking.customer_name}
                                    </p>
                                    <p>
                                        <strong>Email:</strong> {booking.email}
                                    </p>
                                    <p>
                                        <strong>Phone:</strong> {booking.phone}
                                    </p>
                                </div>

                                {/* Licenses */}
                                {booking.license_url && (
                                    <div className="w-full mt-4 flex flex-col">
                                        <div className="mb-2">
                                            <strong>Front License:</strong>
                                            <button
                                                type="button"
                                                onClick={() => openLicenseImage(booking.license_url.split(",")[0])}
                                                className="text-blue-500 underline ml-2"
                                            >
                                                View Front
                                            </button>
                                        </div>
                                        <div>
                                            <strong>Back License:</strong>
                                            <button
                                                type="button"
                                                onClick={() => openLicenseImage(booking.license_url.split(",")[1])}
                                                className="text-blue-500 underline ml-2"
                                            >
                                                View Back
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Cancel Button */}
                                {filter === "booked" && (
                                    <button
                                        onClick={() => changeStatus(booking.id, "cancelled")}
                                        className="mt-4 px-4 py-2 bg-red-500 text-white rounded w-full md:w-auto"
                                    >
                                        Cancel Booking
                                    </button>
                                )}
                                {filter === "cancelled" && (
                                    <button
                                        onClick={() => changeStatus(booking.id, "booked")}
                                        className="mt-4 px-4 py-2 bg-green-500 text-white rounded w-full md:w-auto"
                                    >
                                        Accept Booking
                                    </button>
                                )}
                            </div>
                        ))
                    ) : (
                        <p>No bookings found for the selected filter.</p>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default AdminDashboard;
